import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { projects as projectsApi } from '@/utils/api';
import { ArrowLeft } from 'lucide-react';

import VaultTab from '@/components/project/VaultTab';
import DocumentsTab from '@/components/project/DocumentsTab';
import BugsTab from '@/components/project/BugsTab';
import ChatTab from '@/components/project/ChatTab';
import EstimatesTab from '@/components/project/EstimatesTab';
import InvoicesTab from '@/components/project/InvoicesTab';
import SowsTab from '@/components/project/SowsTab';
import ChangeRequestsTab from '@/components/project/ChangeRequestsTab';
import ZoomMeetings from '@/pages/operator/ZoomMeetings';

const CLIENT_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'vault', label: 'Vault' },
  { id: 'documents', label: 'Documents' },
  { id: 'bugs', label: 'Bug Reports' },
  { id: 'chat', label: 'Chat' },
  { id: 'estimates', label: 'Estimates' },
  { id: 'sows', label: 'SOW' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'calls', label: 'Calls' },
  { id: 'changes', label: 'Change Requests' },
];

const STATUS_COLORS = {
  'New': '#5C6B72', 'Scoping': '#006080', 'Estimate Sent': '#D9A441',
  'Approved': '#3C8A5A', 'In Progress': '#006080', 'UAT': '#0066CC',
  'Delivered': '#3C8A5A', 'Archived': '#8A99A0',
};

function OverviewTab({ project }) {
  return (
    <div className="space-y-5 animate-fade-in">
      <div className="card-base p-6">
        <h3 className="font-bold mb-4" style={{ color: 'var(--ink)' }}>Project Details</h3>
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: 'Status', value: project.status },
            { label: 'Type', value: project.project_type },
            { label: 'Budget', value: project.budget_band || '—' },
            { label: 'Timeline', value: project.timeline || '—' },
          ].map(({ label, value }) => (
            <div key={label}>
              <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--muted)' }}>{label}</p>
              <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>{value}</p>
            </div>
          ))}
        </div>
      </div>

      {project.description && (
        <div className="card-base p-6">
          <h3 className="font-bold mb-3" style={{ color: 'var(--ink)' }}>Description</h3>
          <p className="leading-relaxed" style={{ color: 'var(--muted)' }}>{project.description}</p>
        </div>
      )}

      {project.milestones?.length > 0 && (
        <div className="card-base p-6">
          <h3 className="font-bold mb-4" style={{ color: 'var(--ink)' }}>Milestones</h3>
          <div className="space-y-3">
            {project.milestones.map((m, i) => (
              <div key={i} className="flex items-center gap-3">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ background: m.status === 'completed' ? '#3C8A5A' : m.status === 'in_progress' ? '#006080' : '#E8E3D9' }}
                />
                <div className="flex-1">
                  <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>{m.name}</p>
                </div>
                <p className="text-xs" style={{ color: 'var(--muted)' }}>{m.due_date}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ClientProject() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    projectsApi.get(projectId).then(r => setProject(r.data)).catch(() => {});
  }, [projectId]);

  if (!project) return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <div className="h-32 rounded-card animate-pulse" style={{ background: 'var(--border)' }} />
    </div>
  );

  const statusColor = STATUS_COLORS[project.status] || '#006080';

  return (
    <div>
      {/* Header */}
      <div className="border-b" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
        <div className="max-w-3xl mx-auto px-6 py-5">
          <button
            className="flex items-center gap-1.5 text-sm mb-4 transition-opacity hover:opacity-70"
            style={{ color: 'var(--muted)' }}
            onClick={() => navigate('/portal')}
            data-testid="back-to-portal"
          >
            <ArrowLeft size={14} /> My Projects
          </button>

          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-black" style={{ color: 'var(--ink)' }}>{project.name}</h1>
              <div className="flex items-center gap-2 mt-2">
                <span
                  className="badge-status"
                  style={{ background: `${statusColor}15`, color: statusColor }}
                  data-testid="client-project-status"
                >
                  {project.status}
                </span>
                <span className="text-sm" style={{ color: 'var(--muted)' }}>{project.project_type}</span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-0 mt-5 overflow-x-auto" data-testid="client-project-tabs">
            {CLIENT_TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="px-4 py-2 text-sm font-medium transition-colors border-b-2 whitespace-nowrap"
                style={{
                  borderColor: activeTab === tab.id ? '#006080' : 'transparent',
                  color: activeTab === tab.id ? '#006080' : 'var(--muted)',
                  background: 'transparent',
                }}
                data-testid={`client-tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-6">
        {activeTab === 'overview' && <OverviewTab project={project} />}
        {activeTab === 'vault' && <VaultTab projectId={projectId} isOperator={false} />}
        {activeTab === 'documents' && <DocumentsTab projectId={projectId} isOperator={false} />}
        {activeTab === 'bugs' && <BugsTab projectId={projectId} isOperator={false} />}
        {activeTab === 'chat' && <ChatTab projectId={projectId} isOperator={false} />}
        {activeTab === 'estimates' && <EstimatesTab projectId={projectId} project={project} isOperator={false} />}
        {activeTab === 'sows' && <SowsTab projectId={projectId} isOperator={false} />}
        {activeTab === 'invoices' && <InvoicesTab projectId={projectId} project={project} isOperator={false} />}
        {activeTab === 'calls' && <ZoomMeetings scopedProjectId={projectId} isOperator={false} />}
        {activeTab === 'changes' && <ChangeRequestsTab projectId={projectId} isOperator={false} />}
      </div>
    </div>
  );
}
